import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, RefreshCw, Users, Calendar, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "odysseas_admin_token";

const DAY_INFO = {
  day1: { label: "Day One", title: "Welcome Dinner · Thu 10 Sep" },
  day2: { label: "Day Two", title: "Persian Night · Fri 11 Sep" },
  day3: { label: "Day Three", title: "Holy Baptism & Reception · Sat 12 Sep" },
};

function LoginCard({ onSuccess }) {
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!password) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(TOKEN_KEY, data.token);
      onSuccess();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string" ? detail : "Invalid password"
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <form
        onSubmit={submit}
        className="glass-card rounded-[2rem] p-10 sm:p-12 w-full max-w-md"
        data-testid="admin-login-form"
      >
        <p className="label-eyebrow">Private</p>
        <h1 className="font-serif-display text-4xl text-[#4A5346] mt-4 mb-8">
          Admin
        </h1>
        <label className="label-eyebrow" htmlFor="admin-pw">
          Password
        </label>
        <input
          id="admin-pw"
          type="password"
          className="line-input mt-3"
          placeholder="••••••••"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoFocus
          data-testid="admin-password-input"
        />
        <button
          type="submit"
          className="btn-soft mt-10 w-full"
          disabled={busy}
          data-testid="admin-login-submit"
        >
          {busy ? "Signing in…" : "Enter"}
        </button>
      </form>
    </div>
  );
}

function StatCard({ value, label, sub }) {
  return (
    <div className="glass-card rounded-2xl p-6">
      <div className="font-serif-display text-5xl text-[#4A5346] leading-none">
        {value}
      </div>
      <div className="label-eyebrow mt-3">{label}</div>
      {sub && <div className="text-xs text-[#757E70] mt-2">{sub}</div>}
    </div>
  );
}

function Dashboard({ token, onLogout }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/rsvp`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(data);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Session expired. Please sign in again.");
        onLogout();
      } else {
        toast.error("Could not load RSVPs.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (rsvp) => {
    const ok = window.confirm(
      `Delete RSVP from "${rsvp.name}" (${rsvp.email})?\n\nThis cannot be undone.`
    );
    if (!ok) return;
    try {
      await axios.delete(`${API}/admin/rsvp/${rsvp.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`Deleted ${rsvp.name}`);
      load();
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Session expired. Please sign in again.");
        onLogout();
      } else {
        toast.error("Could not delete the RSVP.");
      }
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading && !data) {
    return (
      <div className="min-h-screen flex items-center justify-center text-[#757E70]">
        Loading…
      </div>
    );
  }

  const totals = data?.totals || {};
  const byDay = totals.by_day || { day1: 0, day2: 0, day3: 0 };
  const byDayNames = totals.by_day_names || { day1: [], day2: [], day3: [] };
  const rsvps = data?.rsvps || [];

  return (
    <div className="min-h-screen py-16 px-6 max-w-6xl mx-auto" data-testid="admin-dashboard">
      <div className="flex items-start sm:items-center justify-between flex-col sm:flex-row gap-4 mb-12">
        <div>
          <p className="label-eyebrow">Private dashboard</p>
          <h1 className="font-serif-display text-4xl sm:text-5xl text-[#4A5346] mt-3">
            RSVP Responses
          </h1>
        </div>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={load}
            className="inline-flex items-center gap-2 text-xs tracking-[0.22em] uppercase text-[#4A5346] border border-[#8FA092]/40 hover:border-[#6F8175] rounded-full px-5 py-3 transition-colors"
            data-testid="admin-refresh"
          >
            <RefreshCw className="w-4 h-4" strokeWidth={1.4} />
            <span>Refresh</span>
          </button>
          <button
            type="button"
            onClick={onLogout}
            className="inline-flex items-center gap-2 text-xs tracking-[0.22em] uppercase text-[#4A5346] border border-[#8FA092]/40 hover:border-[#6F8175] rounded-full px-5 py-3 transition-colors"
            data-testid="admin-logout"
          >
            <LogOut className="w-4 h-4" strokeWidth={1.4} />
            <span>Log out</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-12">
        <StatCard value={totals.total_responses ?? 0} label="Responses" />
        <StatCard
          value={totals.total_guests ?? 0}
          label="Guests"
          sub="Sum of all submissions"
        />
        <StatCard value={byDay.day1} label="Day One" sub="Thu 10 Sep" />
        <StatCard value={byDay.day2} label="Day Two" sub="Fri 11 Sep" />
        <StatCard value={byDay.day3} label="Day Three" sub="Sat 12 Sep" />
      </div>

      {/* Names per day */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
        {["day1", "day2", "day3"].map((dayKey) => (
          <div
            key={dayKey}
            className="glass-card rounded-2xl p-6"
            data-testid={`admin-day-${dayKey}`}
          >
            <div className="flex items-center gap-2 text-[#8FA092]">
              <Calendar className="w-4 h-4" strokeWidth={1.4} />
              <span className="label-eyebrow">{DAY_INFO[dayKey].label}</span>
            </div>
            <h3 className="font-serif-display text-2xl text-[#4A5346] mt-3">
              {DAY_INFO[dayKey].title}
            </h3>
            <p className="text-[#757E70] text-sm mt-2">
              {byDay[dayKey]} guest{byDay[dayKey] === 1 ? "" : "s"} across{" "}
              {byDayNames[dayKey].length} response
              {byDayNames[dayKey].length === 1 ? "" : "s"}
            </p>
            <ul className="mt-4 space-y-2 text-sm">
              {byDayNames[dayKey].length === 0 ? (
                <li className="text-[#757E70] italic">—</li>
              ) : (
                byDayNames[dayKey].map((p, i) => (
                  <li key={i} className="flex justify-between gap-3">
                    <span className="text-[#4A5346]">{p.name}</span>
                    <span className="text-[#757E70]">
                      ×{p.guests}
                    </span>
                  </li>
                ))
              )}
            </ul>
          </div>
        ))}
      </div>

      {/* All responses */}
      <div className="glass-card rounded-2xl overflow-hidden" data-testid="admin-rsvp-table">
        <div className="px-6 py-5 border-b border-[#E2DFD8] flex items-center gap-2 text-[#4A5346]">
          <Users className="w-4 h-4 text-[#8FA092]" strokeWidth={1.4} />
          <span className="label-eyebrow">All RSVPs</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[#8FA092] uppercase text-[11px] tracking-[0.18em]">
              <tr>
                <th className="text-left px-6 py-3 font-medium">Name</th>
                <th className="text-left px-6 py-3 font-medium">Email</th>
                <th className="text-left px-6 py-3 font-medium">Guests</th>
                <th className="text-left px-6 py-3 font-medium">Days</th>
                <th className="text-left px-6 py-3 font-medium">Note</th>
                <th className="text-left px-6 py-3 font-medium">Received</th>
                <th className="text-right px-6 py-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rsvps.length === 0 ? (
                <tr>
                  <td
                    colSpan={7}
                    className="text-center text-[#757E70] py-10 italic"
                  >
                    No responses yet.
                  </td>
                </tr>
              ) : (
                rsvps.map((r) => (
                  <tr
                    key={r.id}
                    className="border-t border-[#E2DFD8]/60 align-top"
                  >
                    <td className="px-6 py-4 text-[#4A5346] font-medium">
                      {r.name}
                    </td>
                    <td className="px-6 py-4 text-[#757E70]">{r.email}</td>
                    <td className="px-6 py-4 text-[#4A5346]">{r.guests}</td>
                    <td className="px-6 py-4 text-[#4A5346]">
                      <div className="flex flex-wrap gap-1">
                        {r.attending_days.map((d) => (
                          <span
                            key={d}
                            className="text-[10px] uppercase tracking-wider bg-[#E8D8CE]/60 border border-[#D4C4B7] rounded-full px-2 py-0.5"
                          >
                            {DAY_INFO[d]?.label || d}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-[#757E70] italic max-w-xs">
                      {r.message || "—"}
                    </td>
                    <td className="px-6 py-4 text-[#757E70] whitespace-nowrap">
                      {new Date(r.created_at).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button
                        type="button"
                        onClick={() => handleDelete(r)}
                        aria-label={`Delete RSVP from ${r.name}`}
                        data-testid={`admin-delete-${r.id}`}
                        className="inline-flex items-center justify-center w-9 h-9 rounded-full text-[#8FA092] hover:text-white hover:bg-[#8FA092] border border-[#8FA092]/40 hover:border-[#8FA092] transition-colors"
                      >
                        <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  };

  if (!token) {
    return <LoginCard onSuccess={() => setToken(localStorage.getItem(TOKEN_KEY))} />;
  }

  return <Dashboard token={token} onLogout={logout} />;
}
